"use client"

import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  `
    group/button inline-flex shrink-0 cursor-pointer items-center justify-center
    rounded-lg border border-transparent bg-clip-padding text-center
    font-semibold wrap-break-word whitespace-normal transition-all outline-none
    select-none
    focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50
    disabled:pointer-events-none disabled:opacity-50
    aria-invalid:border-destructive aria-invalid:ring-[3px]
    aria-invalid:ring-destructive/20
    dark:aria-invalid:border-destructive/50
    dark:aria-invalid:ring-destructive/40
    [&_svg]:pointer-events-none [&_svg]:shrink-0
    [&_svg:not([class*='size-'])]:size-4
  `,
  {
    variants: {
      size: {
        default: `
          gap-1.5 px-5 py-2 text-lg
          has-data-[icon=inline-end]:pr-2
          has-data-[icon=inline-start]:pl-2
        `,
        xs: `
          gap-1 rounded-xl px-3 text-base
          in-data-[slot=button-group]:rounded-lg
          has-data-[icon=inline-end]:pr-1.5
          has-data-[icon=inline-start]:pl-1.5
          [&_svg:not([class*='size-'])]:size-3
        `,
        sm: `
          gap-1 rounded-[min(var(--radius-md),12px)] px-3 py-1 text-sm
          in-data-[slot=button-group]:rounded-lg
          has-data-[icon=inline-end]:pr-1.5
          has-data-[icon=inline-start]:pl-1.5
          [&_svg:not([class*='size-'])]:size-3.5
        `,
        lg: `
          gap-1.5 px-4 py-1.5 text-md
          has-data-[icon=inline-end]:pr-3
          has-data-[icon=inline-start]:pl-3
        `,
        icon: "size-8",
        "icon-xs": `
          size-6 rounded-[min(var(--radius-md),10px)]
          in-data-[slot=button-group]:rounded-lg
          [&_svg:not([class*='size-'])]:size-3
        `,
        "icon-sm": `
          size-7 rounded-[min(var(--radius-md),12px)]
          in-data-[slot=button-group]:rounded-lg
        `,
        "icon-lg": "size-9",
      },
      variant: {
        default: `
          rounded-full border-none border-transparent bg-linear-to-l
          from-pink-700 to-pink-300 text-white no-underline
          hover:opacity-90
          hover:shadow-[0px_2px_4px_-1px_rgba(0,0,0,0.2),0px_4px_5px_0px_rgba(0,0,0,0.14),0px_1px_10px_0px_rgba(0,0,0,0.12)]
        `,
        outline: `
          border-border bg-background no-underline
          hover:bg-muted hover:text-foreground
          aria-expanded:bg-muted aria-expanded:text-foreground
          dark:border-input dark:bg-input/30
          dark:hover:bg-input/50
        `,
        secondary: `
          rounded-full border-2 border-blue-500 bg-blue-500 text-white
          hover:text-pink-500
        `,
        ghost: `
          hover:bg-muted hover:text-foreground
          aria-expanded:bg-muted aria-expanded:text-foreground
          dark:hover:bg-muted/50
        `,
        destructive: `
          bg-destructive/10 text-destructive
          hover:bg-destructive/20
          focus-visible:border-destructive/40 focus-visible:ring-destructive/20
          dark:bg-destructive/20
          dark:hover:bg-destructive/30
          dark:focus-visible:ring-destructive/40
        `,
        linkPrimary: `
          p-0 text-blue-500 underline-offset-4
          hover:underline
        `,
        outlinePink: `
          rounded-full border-2 border-pink-500 bg-transparent text-pink-500
          no-underline
          hover:border-transparent hover:bg-linear-to-l hover:from-pink-700
          hover:to-pink-300 hover:text-white
        `,
        outlineGray: `
          rounded-full border-2 border-gray-500 bg-transparent text-gray-500
          no-underline
          hover:bg-gray-500 hover:text-white
        `,
        outlineWhite: `
          rounded-full border-2 border-white bg-transparent text-white
          no-underline
          hover:bg-white hover:text-pink-500
          hover:shadow-[0px_2px_4px_-1px_rgba(0,0,0,0.2),0px_4px_5px_0px_rgba(0,0,0,0.14),0px_1px_10px_0px_rgba(0,0,0,0.12)]
        `,
        outlineOrange: `
          rounded-full border border-orange-200 bg-orange-50 text-red-800
          no-underline
          hover:border-transparent hover:bg-orange-600 hover:text-white
        `,
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

const Button = ({
  className,
  variant = "default",
  size = "default",
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) => {
  return (
    <ButtonPrimitive
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}
Button.displayName = "Button"

export { Button, buttonVariants }
